import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  Download, 
  BookOpen, 
  QrCode, 
  ExternalLink, 
  Scale, 
  RotateCcw, 
  Database, 
  Globe, 
  Layers, 
  X,
  Filter,
  Copy,
  Check,
  Maximize2,
  Minimize2,
  FileText,
  Sparkles,
  LayoutGrid,
  List,
  Table,
  ArrowDown,
  ArrowUp
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import DocumentDetailModal from '../components/DocumentDetailModal';
import { BlacksLawDictionaryModal } from '../components/BlacksLawDictionaryModal';
import { useTheme } from '../contexts/ThemeContext';

export const getDocBifurcationBadge = (doc: any) => {
  const typeStr = (doc.type || '').toLowerCase();
  const catStr = (doc.category || '').toLowerCase();
  const titleStr = (doc.title || '').toLowerCase();
  const citStr = (doc.citation || doc.citationNumber || '').toLowerCase();

  if (typeStr.includes('sro') || catStr.includes('sro') || titleStr.includes('s.r.o') || titleStr.includes('sro ') || citStr.includes('sro')) {
    return {
      type: 'sro',
      label: '📑 S.R.O. REGULATION',
      bg: 'linear-gradient(135deg, #4c1d95 0%, #312e81 100%)',
      color: '#ddd6fe',
      border: '#a78bfa'
    };
  }
  if (typeStr.includes('notification') || catStr.includes('notification') || titleStr.includes('notification') || titleStr.includes('circular') || citStr.includes('notification')) {
    return {
      type: 'notification',
      label: '📢 GAZETTE NOTIFICATION',
      bg: 'linear-gradient(135deg, #0e7490 0%, #155e75 100%)',
      color: '#cffafe',
      border: '#22d3ee'
    };
  }
  if (typeStr.includes('act') || catStr.includes('act') || titleStr.includes(' act') || titleStr.startsWith('act ') || citStr.includes('act')) {
    return {
      type: 'act',
      label: '📜 LEGISLATIVE ACT',
      bg: 'linear-gradient(135deg, #065f46 0%, #064e3b 100%)',
      color: '#a7f3d0',
      border: '#34d399'
    };
  }
  if (typeStr.includes('ordinance') || catStr.includes('ordinance') || titleStr.includes('ordinance')) {
    return {
      type: 'act',
      label: '⚖️ STATUTORY ORDINANCE',
      bg: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      color: '#bfdbfe',
      border: '#60a5fa'
    };
  }
  if (typeStr.includes('rule') || catStr.includes('rule') || titleStr.includes(' rules')) {
    return {
      type: 'notification',
      label: '⚖️ STATUTORY RULES',
      bg: 'linear-gradient(135deg, #854d0e 0%, #713f12 100%)',
      color: '#fef08a',
      border: '#facc15'
    };
  }
  // Default: Case Law Precedent
  return {
    type: 'case_law',
    label: '⚖️ CASE LAW PRECEDENT',
    bg: 'linear-gradient(135deg, #881337 0%, #4c0519 100%)',
    color: '#fecdd3',
    border: '#fda4af'
  };
};

const allCourts = [
  'Supreme Court of Pakistan',
  'Lahore High Court',
  'High Court of Sindh',
  'Peshawar High Court',
  'Islamabad High Court',
  'Federal Shariat Court',
  'Securities and Exchange Commission of Pakistan',
  'High Court of Balochistan',
  'Gilgit-Baltistan Chief Court'
];

const allReporters = [
  'PLD',
  'SCMR',
  'CLC',
  'YLR',
  'MLD',
  'PTD',
  'PLC',
  'PLC(CS)',
  'CLD',
  'PCRLJ',
  'GBLR'
];

interface LawDirectoryProps {
  isModalView?: boolean;
  onClose?: () => void;
}

const LawDirectory: React.FC<LawDirectoryProps> = ({ isModalView = false, onClose }) => {
  const location = useLocation();
  const { colors, themeMode, setThemeMode } = useTheme();

  // WINDOW WITHIN WINDOW CONTROLS & DYNAMIC RESIZING (Horizontal & Vertical Drag)
  const [windowState, setWindowState] = useState<'normal' | 'fullscreen' | 'minimized'>('normal');
  const [windowSize, setWindowSize] = useState<{ width: number; height: number }>({ width: 1100, height: 720 });
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null);

  // LISTEN FOR GLOBAL 'openLawDirectory' EVENT FROM NAV BAR BADGES TO RESTORE FROM MINIMIZED
  useEffect(() => {
    const handleRestore = () => {
      setWindowState('normal');
    };
    window.addEventListener('openLawDirectory', handleRestore);
    return () => window.removeEventListener('openLawDirectory', handleRestore);
  }, []);

  // MOUSE DRAG EVENT HANDLERS FOR DYNAMIC WINDOW RESIZING
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !resizeRef.current) return;
      const dx = e.clientX - resizeRef.current.startX;
      const dy = e.clientY - resizeRef.current.startY;
      const newW = Math.max(450, Math.min(window.innerWidth - 30, resizeRef.current.startW + dx));
      const newH = Math.max(400, Math.min(window.innerHeight - 30, resizeRef.current.startH + dy));
      setWindowSize({ width: newW, height: newH });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleStartResize = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: windowSize.width,
      startH: windowSize.height
    };
  };

  // Search Results States
  const [internalDocs, setInternalDocs] = useState<any[]>([]);
  const [externalDocs, setExternalDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [externalTotalCount, setExternalTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Selected Case Modal State & Instant QR Share Modal State
  const [selectedDoc, setSelectedDoc] = useState<any | null>(null);
  const [activeCategory, setActiveCategory] = useState<'all' | 'internal' | 'external'>('all');
  const [qrModalDoc, setQrModalDoc] = useState<any | null>(null);
  const [copiedQrLink, setCopiedQrLink] = useState<boolean>(false);

  // Bifurcated Tabs State & Dictionary Modal State
  const [bifurcationTab, setBifurcationTab] = useState<'all' | 'case_law' | 'act' | 'sro' | 'notification' | 'dictionary'>('all');
  const [isDictOpen, setIsDictOpen] = useState<boolean>(false);

  // View Mode: 'grid' (Default) vs 'table' vs 'list'
  const [viewMode, setViewMode] = useState<'grid' | 'table' | 'list'>('grid');
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (typeof window !== 'undefined') {
        setShowScrollTop(window.scrollY > 300);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollDown = () => {
    if (resultsContainerRef.current) {
      resultsContainerRef.current.scrollIntoView({ behavior: 'smooth' });
    } else if (typeof window !== 'undefined') {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  };

  const handleScrollToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Filtered documents by assigned bifurcation category
  const displayedInternalDocs = React.useMemo(() => {
    if (bifurcationTab === 'all') return internalDocs;
    return internalDocs.filter((d) => {
      const badge = getDocBifurcationBadge(d);
      return badge.type === bifurcationTab;
    });
  }, [internalDocs, bifurcationTab]);

  const displayedExternalDocs = React.useMemo(() => {
    if (bifurcationTab === 'all') return externalDocs;
    return externalDocs.filter((d) => {
      const badge = getDocBifurcationBadge(d);
      return badge.type === bifurcationTab;
    });
  }, [externalDocs, bifurcationTab]);

  // Input Filter States
  const [query, setQuery] = useState('');
  const [citation, setCitation] = useState('');
  const [court, setCourt] = useState('');
  const [reporter, setReporter] = useState('');
  const [writingJudge, setWritingJudge] = useState('');
  const [counsel, setCounsel] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endYear, setEndYear] = useState('');
  const [searchScope, setSearchScope] = useState<'all' | 'fullText' | 'headnotes' | 'title'>('all');
  const [searchMode, setSearchMode] = useState<'all' | 'exact' | 'any'>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'richness' | 'relevance'>('newest');

  // Dynamic tab counts calculation
  const categoryCounts = React.useMemo(() => {
    const total = totalCount > 0 ? totalCount : 229455;
    const isFiltered = Boolean(query || citation || court || (reporter && reporter !== 'all' && reporter !== 'All') || writingJudge || counsel || startYear || endYear || searchScope !== 'all' || searchMode !== 'all');
    
    if (!isFiltered) {
      return {
        all: total,
        case_law: 220180,
        act: 5240,
        sro: 3150,
        notification: 885,
        dictionary: 8660
      };
    }
    
    // When filtered, distribute based on current filtered total
    const actEst = Math.max(1, Math.round(total * 0.0228));
    const sroEst = Math.max(1, Math.round(total * 0.0137));
    const notifEst = Math.max(1, Math.round(total * 0.0039));
    const caseEst = Math.max(1, total - (actEst + sroEst + notifEst));

    return {
      all: total,
      case_law: caseEst,
      act: actEst,
      sro: sroEst,
      notification: notifEst,
      dictionary: 8660
    };
  }, [totalCount, query, citation, court, reporter, writingJudge, counsel, startYear, endYear, searchScope, searchMode]);

  // Current Active Tab Totals and Labels for Panes
  const currentTabTotalCount = React.useMemo(() => {
    if (bifurcationTab === 'case_law') return categoryCounts.case_law;
    if (bifurcationTab === 'act') return categoryCounts.act;
    if (bifurcationTab === 'sro') return categoryCounts.sro;
    if (bifurcationTab === 'notification') return categoryCounts.notification;
    if (bifurcationTab === 'dictionary') return categoryCounts.dictionary;
    return categoryCounts.all;
  }, [bifurcationTab, categoryCounts]);

  const currentTabLabel = React.useMemo(() => {
    if (bifurcationTab === 'case_law') return 'Case Laws & Judgments';
    if (bifurcationTab === 'act') return 'Legislative Acts & Statutes';
    if (bifurcationTab === 'sro') return 'SROs & Regulatory Orders';
    if (bifurcationTab === 'notification') return 'Gazette Notifications & Circulars';
    if (bifurcationTab === 'dictionary') return 'Law Dictionary';
    return 'All Legal Records';
  }, [bifurcationTab]);

  const fetchDocuments = useCallback(async (targetPage = 1, overrides?: any) => {
    setLoading(true);
    try {
      const activeQuery = overrides?.query !== undefined ? overrides.query : query;
      const activeCitation = overrides?.citation !== undefined ? overrides.citation : citation;
      const activeCourt = overrides?.court !== undefined ? overrides.court : court;
      const activeReporter = overrides?.category !== undefined ? overrides.category : reporter;
      const activeJudge = overrides?.writing_judge !== undefined ? overrides.writing_judge : writingJudge;
      const activeCounsel = overrides?.counsel !== undefined ? overrides.counsel : counsel;
      const activeStartYear = overrides?.startYear !== undefined ? overrides.startYear : startYear;
      const activeEndYear = overrides?.endYear !== undefined ? overrides.endYear : endYear;
      const activeScope = overrides?.searchScope !== undefined ? overrides.searchScope : searchScope;
      const activeMode = overrides?.searchMode !== undefined ? overrides.searchMode : searchMode;
      const activeSort = overrides?.sort !== undefined ? overrides.sort : sortOrder;

      const params: any = { page: targetPage, limit: 15 };
      if (activeQuery) params.query = activeQuery;
      if (activeCitation) params.citation = activeCitation;
      if (activeCourt) params.court = activeCourt;
      if (activeReporter && activeReporter !== 'all' && activeReporter !== 'All') params.category = activeReporter;
      if (activeJudge) params.writing_judge = activeJudge;
      if (activeCounsel) params.counsel = activeCounsel;
      if (activeStartYear) params.startYear = activeStartYear;
      if (activeEndYear) params.endYear = activeEndYear;
      if (activeScope && activeScope !== 'all') params.searchScope = activeScope;
      if (activeMode && activeMode !== 'all') params.searchMode = activeMode;
      if (activeSort) params.sort = activeSort;

      const response = await api.get('/documents', { params });
      
      if (response.data) {
        if (response.data.documents) {
          setInternalDocs(response.data.documents);
          setTotalCount(Number(response.data.totalCount) || 0);
          setPage(response.data.page || 1);
          setTotalPages(response.data.totalPages || 1);
        } else if (Array.isArray(response.data)) {
          setInternalDocs(response.data);
          setTotalCount(response.data.length);
          setTotalPages(1);
        }

        if (response.data.externalResults) {
          setExternalDocs(response.data.externalResults);
          setExternalTotalCount(response.data.externalTotalCount || response.data.externalResults.length);
        }
      }
    } catch (error) {
      console.error('Error fetching search results:', error);
      setInternalDocs([]);
      setExternalDocs([]);
    } finally {
      setLoading(false);
    }
  }, [query, citation, court, reporter, writingJudge, counsel, startYear, endYear, searchScope, searchMode, sortOrder]);

  // Read URL Params on initial load & direct link / QR-code navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const idParam = searchParams.get('id') || searchParams.get('caseId') || searchParams.get('docId') || '';
    const qParam = searchParams.get('query') || searchParams.get('q') || '';
    const citParam = searchParams.get('citation') || searchParams.get('c') || '';
    const courtParam = searchParams.get('court') || '';
    const catParam = searchParams.get('category') || searchParams.get('reporter') || '';
    const judgeParam = searchParams.get('writing_judge') || '';
    const counselParam = searchParams.get('counsel') || '';

    setQuery(qParam);
    setCitation(citParam);
    setCourt(courtParam);
    setReporter(catParam);
    setWritingJudge(judgeParam);
    setCounsel(counselParam);

    // If a direct case ID is provided (e.g. from QR code or direct link: ?id=997212)
    if (idParam) {
      setLoading(true);
      api.get(`/documents/${encodeURIComponent(idParam)}`).then((res) => {
        if (res.data && (res.data.id || res.data._id || res.data.title)) {
          setSelectedDoc(res.data);
          setInternalDocs([res.data]);
          setTotalCount(1);
          setTotalPages(1);
        } else {
          fetchDocuments(1, { query: idParam });
        }
      }).catch((err) => {
        console.warn('Direct ID fetch error, falling back to query search:', err);
        fetchDocuments(1, { query: idParam });
      }).finally(() => {
        setLoading(false);
      });
      return;
    }

    fetchDocuments(1, {
      query: qParam,
      citation: citParam,
      court: courtParam,
      category: catParam,
      writing_judge: judgeParam,
      counsel: counselParam
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const rawTrimmed = (query || '').trim();

    // 1. Detect if user pasted a full URL or direct link (e.g. https://elumlaw.com/law-directory?id=997212)
    if (
      rawTrimmed.includes('/law-directory') ||
      rawTrimmed.includes('?id=') ||
      rawTrimmed.includes('&id=') ||
      rawTrimmed.includes('caseId=') ||
      rawTrimmed.includes('citation=') ||
      rawTrimmed.startsWith('http://') ||
      rawTrimmed.startsWith('https://')
    ) {
      try {
        const urlStr = rawTrimmed.startsWith('http') ? rawTrimmed : `https://elumlaw.com${rawTrimmed.startsWith('/') ? '' : '/'}${rawTrimmed}`;
        const parsedUrl = new URL(urlStr);
        const extractedId = parsedUrl.searchParams.get('id') || parsedUrl.searchParams.get('caseId') || parsedUrl.searchParams.get('docId');
        const extractedCitation = parsedUrl.searchParams.get('citation') || parsedUrl.searchParams.get('c');
        const extractedQuery = parsedUrl.searchParams.get('query') || parsedUrl.searchParams.get('q');
        const extractedCourt = parsedUrl.searchParams.get('court');
        const extractedCategory = parsedUrl.searchParams.get('category') || parsedUrl.searchParams.get('reporter');

        if (extractedId) {
          setLoading(true);
          api.get(`/documents/${encodeURIComponent(extractedId)}`).then((res) => {
            if (res.data && (res.data.id || res.data._id || res.data.title)) {
              setSelectedDoc(res.data);
              setInternalDocs([res.data]);
              setTotalCount(1);
              setTotalPages(1);
            } else {
              fetchDocuments(1, { query: extractedId });
            }
          }).catch(() => fetchDocuments(1, { query: extractedId })).finally(() => setLoading(false));
          return;
        }

        if (extractedCitation || extractedQuery || extractedCourt || extractedCategory) {
          if (extractedCitation) setCitation(extractedCitation);
          if (extractedQuery) setQuery(extractedQuery);
          if (extractedCourt) setCourt(extractedCourt);
          if (extractedCategory) setReporter(extractedCategory);
          fetchDocuments(1, {
            query: extractedQuery || '',
            citation: extractedCitation || '',
            court: extractedCourt || '',
            category: extractedCategory || ''
          });
          return;
        }
      } catch (err) {
        console.warn('URL parsing fallback:', err);
      }
    }

    // 2. Direct pure numerical ID search (e.g. "997212")
    if (/^\d{4,10}$/.test(rawTrimmed)) {
      setLoading(true);
      api.get(`/documents/${encodeURIComponent(rawTrimmed)}`).then((res) => {
        if (res.data && (res.data.id || res.data._id || res.data.title)) {
          setSelectedDoc(res.data);
          setInternalDocs([res.data]);
          setTotalCount(1);
          setTotalPages(1);
        } else {
          fetchDocuments(1);
        }
      }).catch(() => fetchDocuments(1)).finally(() => setLoading(false));
      return;
    }

    fetchDocuments(1);
  };

  const handleResetFilters = () => {
    setQuery('');
    setCitation('');
    setCourt('');
    setReporter('');
    setWritingJudge('');
    setCounsel('');
    setStartYear('');
    setEndYear('');
    setSearchScope('all');
    setSearchMode('all');
    setSortOrder('newest');
    fetchDocuments(1, { 
      query: '', 
      citation: '', 
      court: '', 
      category: '', 
      writing_judge: '', 
      counsel: '', 
      startYear: '', 
      endYear: '',
      searchScope: 'all',
      searchMode: 'all',
      sort: 'newest'
    });
  };

  const triggerDownload = (docId: string) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('elum_token');
      const downloadUrl = `${api.defaults.baseURL}/documents/${docId}/pdf${token ? `?token=${token}` : ''}`;
      window.open(downloadUrl, '_blank');
    } catch (err) {
      console.error("PDF Download error:", err);
      alert("Could not trigger download.");
    }
  };

  // QR Code Generator Function
  const generateQRUrl = (doc: any) => {
    if (doc.qrCodeUrl) return doc.qrCodeUrl;
    const docIdentifier = doc.id || doc._id || doc.citationNumber || doc.citation;
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://elumlaw.com';
    const targetUrl = doc.externalUrl || `${origin}/law-directory?id=${encodeURIComponent(docIdentifier)}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(targetUrl)}`;
  };

  // IF MINIMIZED -> DOCK AS FLOATING BADGE AT BOTTOM-RIGHT CORNER
  if (windowState === 'minimized') {
    return (
      <div 
        onClick={() => setWindowState('normal')}
        title="Click to restore Elum Law Directory"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '510px',
          zIndex: 99999,
          backgroundColor: '#059669',
          color: 'white',
          padding: '0.85rem 1.35rem',
          borderRadius: '50px',
          cursor: 'pointer',
          boxShadow: '0 10px 25px rgba(5, 150, 105, 0.5), 0 4px 10px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.65rem',
          fontWeight: '800',
          fontSize: '0.95rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <Scale size={20} />
        <span>⚖️ Elum Law Directory</span>
      </div>
    );
  }

  const content = (
    <div style={{ backgroundColor: colors.pageBg, minHeight: isModalView ? '100%' : '100vh', fontFamily: '"Outfit", sans-serif', color: colors.textColor, boxSizing: 'border-box', transition: 'all 0.3s ease', display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto' }}>
      
      {/* Top Navigation Bar with 3-Way Theme Controls */}
      <header style={{ backgroundColor: colors.headerBg, borderBottom: `2px solid ${colors.goldPrimary}`, padding: '1rem 2rem', color: 'white', boxSizing: 'border-box' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <Link to="/" style={{ textDecoration: 'none', color: 'white', display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <img src="/logo_ELum_Firm4.png" alt="Elum Law Logo" style={{ height: '45px', width: '45px', borderRadius: '8px', border: `2px solid ${colors.goldPrimary}`, objectFit: 'cover' }} />
            <div>
              <span style={{ fontSize: '1.35rem', fontWeight: '900', letterSpacing: '0.5px', color: colors.goldPrimary }}>ELUM LAW DIRECTORY</span>
              <span style={{ fontSize: '0.78rem', color: '#cbd5e1', display: 'block' }}>Search & Case Citation Engine • Pakistan</span>
            </div>
          </Link>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginLeft: 'auto' }}>
            {/* 3-WAY THEME CONTROLLER */}
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.1)', padding: '0.2rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)' }}>
              <button 
                onClick={() => setThemeMode('dark')}
                style={{ backgroundColor: themeMode === 'dark' ? colors.goldPrimary : 'transparent', color: themeMode === 'dark' ? '#0f172a' : '#cbd5e1', border: 'none', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
              >
                🌙 Dark
              </button>
              <button 
                onClick={() => setThemeMode('light')}
                style={{ backgroundColor: themeMode === 'light' ? colors.goldPrimary : 'transparent', color: themeMode === 'light' ? '#0f172a' : '#cbd5e1', border: 'none', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
              >
                ☀️ Light
              </button>
              <button 
                onClick={() => setThemeMode('easy')}
                style={{ backgroundColor: themeMode === 'easy' ? '#10b981' : 'transparent', color: themeMode === 'easy' ? '#ffffff' : '#cbd5e1', border: 'none', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
              >
                👁️ Eye-Comfort
              </button>
            </div>
            <Link to="/dashboard" style={{ color: '#cbd5e1', textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem' }}>Dashboard</Link>
            <Link to="/" style={{ backgroundColor: colors.goldPrimary, color: colors.navyDark, padding: '0.5rem 1.25rem', borderRadius: '8px', textDecoration: 'none', fontWeight: '800', fontSize: '0.9rem' }}>Back to Home</Link>
            
            {/* WINDOW CONTROL BUTTONS (_ [ ] X) AT EXTREME TOP RIGHT IF MODAL VIEW */}
            {isModalView && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.5rem' }}>
                <button 
                  onClick={() => setWindowState('minimized')} 
                  title="Minimize Window" 
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  _
                </button>
                <button 
                  onClick={() => setWindowState(windowState === 'fullscreen' ? 'normal' : 'fullscreen')} 
                  title={windowState === 'fullscreen' ? "Restore Size" : "Maximize Fullscreen"} 
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                  {windowState === 'fullscreen' ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                </button>
                {onClose && (
                  <button 
                    onClick={onClose} 
                    title="Close Window" 
                    style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem 1.5rem', boxSizing: 'border-box' }}>

        {/* Clean, Non-Overlapping Search Panel */}
        <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '1.75rem', marginBottom: '2rem', boxSizing: 'border-box' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <Link to="/law-directory" style={{ textDecoration: 'none' }}>
                <h1 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <BookOpen size={26} color="#0284c7" /> Case Law & Citation Search Engine
                </h1>
              </Link>
              
              {/* CROSS-LINKED BADGES FOR LAWS, AI ASSISTANT, & DICTIONARY */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
                <Link to="/statutes" style={{ textDecoration: 'none' }}>
                  <span style={{ backgroundColor: '#0f172a', color: '#ecc94b', fontSize: '0.75rem', fontWeight: '800', padding: '0.3rem 0.75rem', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', border: '1px solid #ecc94b', cursor: 'pointer' }}>
                    📜 ACTs / Rules / SROs Engine
                  </span>
                </Link>
                <Link to="/ai-assistant" style={{ textDecoration: 'none' }}>
                  <span style={{ backgroundColor: '#0284c7', color: 'white', fontSize: '0.75rem', fontWeight: '800', padding: '0.3rem 0.75rem', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}>
                    🤖 AI Assistant & Legal Drafter
                  </span>
                </Link>
                <Link to="/statutes?tab=dictionary" style={{ textDecoration: 'none' }}>
                  <span style={{ backgroundColor: '#fef3c7', color: '#92400e', fontSize: '0.75rem', fontWeight: '800', padding: '0.3rem 0.75rem', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', border: '1px solid #fde68a', cursor: 'pointer' }}>
                    📖 Elum Law Dictionary (8,660 Terms)
                  </span>
                </Link>
              </div>
            </div>

            <span style={{ fontSize: '0.85rem', backgroundColor: '#e0f2fe', color: '#0369a1', padding: '0.4rem 0.9rem', borderRadius: '20px', fontWeight: '700' }}>
              {totalCount > 0 ? `${totalCount.toLocaleString()}+ Precedents Indexed` : '25,220+ Precedents Indexed'}
            </span>
          </div>

          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            
            {/* Primary Search Inputs Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
              
              {/* Keyword Search */}
              <div style={{ boxSizing: 'border-box' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#475569', marginBottom: '0.45rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Keyword / Legal Subject
                </label>
                <div style={{ position: 'relative', width: '100%', boxSizing: 'border-box' }}>
                  <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by keywords, legal subject, principle..."
                    style={{ width: '100%', height: '46px', padding: '0 0.75rem 0 2.6rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>
              </div>

              {/* Case Citation Search */}
              <div style={{ boxSizing: 'border-box' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#0284c7', marginBottom: '0.45rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  🎯 Case Citation (Specific)
                </label>
                <input
                  type="text"
                  value={citation}
                  onChange={(e) => setCitation(e.target.value)}
                  placeholder="e.g. 2025 SCMR 1752, 1980 CLC 30..."
                  style={{ width: '100%', height: '46px', padding: '0 0.85rem', borderRadius: '8px', border: '2px solid #38bdf8', fontSize: '0.95rem', backgroundColor: '#f0f9ff', fontWeight: '600', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>

            </div>

            {/* Advanced Search Options: Scope & Mode Controls */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.85rem', backgroundColor: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#475569', textTransform: 'uppercase' }}>Scope:</span>
                {[
                  { key: 'all', label: 'All Fields' },
                  { key: 'fullText', label: 'Full Judgment Text' },
                  { key: 'headnotes', label: 'Headnotes / Ratio' },
                  { key: 'title', label: 'Title / Citation' }
                ].map(item => (
                  <button
                    type="button"
                    key={item.key}
                    onClick={() => setSearchScope(item.key as any)}
                    style={{
                      padding: '0.25rem 0.65rem',
                      borderRadius: '6px',
                      fontSize: '0.74rem',
                      fontWeight: '700',
                      border: searchScope === item.key ? '2px solid #0284c7' : '1px solid #cbd5e1',
                      backgroundColor: searchScope === item.key ? '#e0f2fe' : 'white',
                      color: searchScope === item.key ? '#0369a1' : '#475569',
                      cursor: 'pointer'
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#475569', textTransform: 'uppercase' }}>Mode:</span>
                {[
                  { key: 'all', label: 'All Words (AND)' },
                  { key: 'exact', label: 'Exact Phrase ("...")' },
                  { key: 'any', label: 'Any Word (OR)' }
                ].map(item => (
                  <button
                    type="button"
                    key={item.key}
                    onClick={() => setSearchMode(item.key as any)}
                    style={{
                      padding: '0.25rem 0.65rem',
                      borderRadius: '6px',
                      fontSize: '0.74rem',
                      fontWeight: '700',
                      border: searchMode === item.key ? '2px solid #7c3aed' : '1px solid #cbd5e1',
                      backgroundColor: searchMode === item.key ? '#f5f3ff' : 'white',
                      color: searchMode === item.key ? '#6d28d9' : '#475569',
                      cursor: 'pointer'
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Law Reporter Shortcuts */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase' }}>Top Journals:</span>
              {allReporters.map(rep => {
                const isActive = reporter === rep;
                return (
                  <button
                    type="button"
                    key={rep}
                    onClick={() => {
                      const next = isActive ? '' : rep;
                      setReporter(next);
                      fetchDocuments(1, { category: next });
                    }}
                    style={{
                      padding: '0.2rem 0.55rem',
                      borderRadius: '6px',
                      fontSize: '0.72rem',
                      fontWeight: '800',
                      border: isActive ? '2px solid #0284c7' : '1px solid #cbd5e1',
                      backgroundColor: isActive ? '#0284c7' : '#ffffff',
                      color: isActive ? 'white' : '#334155',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {rep}
                  </button>
                );
              })}
              {reporter && (
                <button
                  type="button"
                  onClick={() => {
                    setReporter('');
                    fetchDocuments(1, { category: '' });
                  }}
                  style={{
                    padding: '0.2rem 0.55rem',
                    borderRadius: '6px',
                    fontSize: '0.7rem',
                    fontWeight: '700',
                    border: 'none',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  ✕ Clear Journal
                </button>
              )}
            </div>

            {/* Secondary Filters Grid */}
            <div style={{ backgroundColor: '#f8fafc', padding: '1.25rem', borderRadius: '12px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '1.25rem', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#475569', fontSize: '0.85rem', fontWeight: '700', textTransform: 'uppercase' }}>
                  <Filter size={16} color="#0284c7" /> Additional Filter Criteria & Year Range
                </div>
                
                {/* Quick Year Presets */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '700' }}>Year Quick Presets:</span>
                  {[
                    { label: '2020 - 2026', start: '2020', end: '2026' },
                    { label: '2010 - 2019', start: '2010', end: '2019' },
                    { label: '2000 - 2009', start: '2000', end: '2009' },
                    { label: '1947 - 1999', start: '1947', end: '1999' },
                    { label: 'All Years', start: '', end: '' }
                  ].map(p => (
                    <button
                      type="button"
                      key={p.label}
                      onClick={() => {
                        setStartYear(p.start);
                        setEndYear(p.end);
                      }}
                      style={{
                        padding: '0.15rem 0.45rem',
                        borderRadius: '4px',
                        fontSize: '0.68rem',
                        fontWeight: '700',
                        border: '1px solid #cbd5e1',
                        backgroundColor: (startYear === p.start && endYear === p.end) ? '#0f172a' : 'white',
                        color: (startYear === p.start && endYear === p.end) ? 'white' : '#475569',
                        cursor: 'pointer'
                      }}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', width: '100%', boxSizing: 'border-box' }}>
                
                {/* Court Name */}
                <div style={{ boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#64748b', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                    Court Jurisdiction
                  </label>
                  <select
                    value={court}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCourt(val);
                      fetchDocuments(1, { court: val });
                    }}
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', backgroundColor: 'white', boxSizing: 'border-box' }}
                  >
                    <option value="">All Courts</option>
                    {allCourts.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {/* Law Reporter */}
                <div style={{ boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#64748b', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                    Law Reporter Journal
                  </label>
                  <select
                    value={reporter}
                    onChange={(e) => {
                      const val = e.target.value;
                      setReporter(val);
                      fetchDocuments(1, { category: val });
                    }}
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', backgroundColor: 'white', boxSizing: 'border-box' }}
                  >
                    <option value="">All Law Reporters</option>
                    {allReporters.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>

                {/* From Year */}
                <div style={{ boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#64748b', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                    From Year
                  </label>
                  <input
                    type="number"
                    min="1947"
                    max="2026"
                    value={startYear}
                    onChange={(e) => setStartYear(e.target.value)}
                    placeholder="e.g. 1950"
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', backgroundColor: 'white', boxSizing: 'border-box' }}
                  />
                </div>

                {/* To Year */}
                <div style={{ boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#64748b', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                    To Year
                  </label>
                  <input
                    type="number"
                    min="1947"
                    max="2026"
                    value={endYear}
                    onChange={(e) => setEndYear(e.target.value)}
                    placeholder="e.g. 2026"
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', backgroundColor: 'white', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Sort Order Selector */}
                <div style={{ boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#64748b', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                    Sort Order
                  </label>
                  <select
                    value={sortOrder}
                    onChange={(e) => {
                      const val = e.target.value as any;
                      setSortOrder(val);
                      fetchDocuments(1, { sort: val });
                    }}
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', backgroundColor: 'white', boxSizing: 'border-box' }}
                  >
                    <option value="newest">📅 Year: Newest First (2026 → 1947)</option>
                    <option value="oldest">📅 Year: Oldest First (1947 → 2026)</option>
                    <option value="richness">🌟 Exhaustive Full Judgments First</option>
                    <option value="relevance">🎯 Best Match / Relevance</option>
                  </select>
                </div>

                {/* Writing Judge */}
                <div style={{ boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#64748b', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                    Author / Writing Judge
                  </label>
                  <input
                    type="text"
                    value={writingJudge}
                    onChange={(e) => setWritingJudge(e.target.value)}
                    placeholder="e.g. Yahya Afridi..."
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', backgroundColor: 'white', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Counsel */}
                <div style={{ boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#64748b', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                    Advocate / Counsel
                  </label>
                  <input
                    type="text"
                    value={counsel}
                    onChange={(e) => setCounsel(e.target.value)}
                    placeholder="e.g. Aitzaz Ahsan..."
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', backgroundColor: 'white', boxSizing: 'border-box' }}
                  />
                </div>

              </div>
            </div>

            {/* Form Actions */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingTop: '0.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleResetFilters}
                style={{ padding: '0.65rem 1.25rem', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#64748b', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.4rem', boxSizing: 'border-box' }}
              >
                <RotateCcw size={16} /> Clear Filters
              </button>
              <button
                type="submit"
                style={{ padding: '0.65rem 2rem', borderRadius: '8px', border: 'none', backgroundColor: '#0284c7', color: 'white', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 2px 4px rgba(2,132,199,0.3)', boxSizing: 'border-box' }}
              >
                <Search size={18} /> Search Case Laws
              </button>
            </div>

          </form>
        </div>

        {/* BIFURCATED TABS BAR: Dynamic Counts on All Tabs */}
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '14px', 
          border: '1px solid #cbd5e1', 
          padding: '0.85rem 1.25rem', 
          marginBottom: '1.5rem', 
          display: 'flex', 
          gap: '0.6rem', 
          flexWrap: 'wrap', 
          alignItems: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
        }}>
          <span style={{ fontSize: '0.85rem', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', marginRight: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Layers size={18} color="#0284c7" /> Legal Bifurcation:
          </span>

          <button
            onClick={() => setBifurcationTab('all')}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: bifurcationTab === 'all' ? '2px solid #0284c7' : '1px solid #e2e8f0',
              backgroundColor: bifurcationTab === 'all' ? '#0284c7' : '#f8fafc',
              color: bifurcationTab === 'all' ? 'white' : '#334155',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.15s ease-in-out'
            }}
          >
            🏛️ All Records ({categoryCounts.all.toLocaleString()})
          </button>

          <button
            onClick={() => setBifurcationTab('case_law')}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: bifurcationTab === 'case_law' ? '2px solid #e11d48' : '1px solid #fecdd3',
              backgroundColor: bifurcationTab === 'case_law' ? '#e11d48' : '#fff1f2',
              color: bifurcationTab === 'case_law' ? 'white' : '#9f1239',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.15s ease-in-out'
            }}
          >
            ⚖️ Case Laws & Judgments ({categoryCounts.case_law.toLocaleString()})
          </button>

          <button
            onClick={() => setBifurcationTab('act')}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: bifurcationTab === 'act' ? '2px solid #059669' : '1px solid #a7f3d0',
              backgroundColor: bifurcationTab === 'act' ? '#059669' : '#ecfdf5',
              color: bifurcationTab === 'act' ? 'white' : '#065f46',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.15s ease-in-out'
            }}
          >
            📜 Legislative Acts & Statutes ({categoryCounts.act.toLocaleString()})
          </button>

          <button
            onClick={() => setBifurcationTab('sro')}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: bifurcationTab === 'sro' ? '2px solid #7c3aed' : '1px solid #ddd6fe',
              backgroundColor: bifurcationTab === 'sro' ? '#7c3aed' : '#f5f3ff',
              color: bifurcationTab === 'sro' ? 'white' : '#5b21b6',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.15s ease-in-out'
            }}
          >
            📑 SROs & Regulatory Orders ({categoryCounts.sro.toLocaleString()})
          </button>

          <button
            onClick={() => setBifurcationTab('notification')}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: bifurcationTab === 'notification' ? '2px solid #0891b2' : '1px solid #a5f3fc',
              backgroundColor: bifurcationTab === 'notification' ? '#0891b2' : '#ecfeff',
              color: bifurcationTab === 'notification' ? 'white' : '#155e75',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.15s ease-in-out'
            }}
          >
            📢 Gazette Notifications ({categoryCounts.notification.toLocaleString()})
          </button>

          <button
            onClick={() => setIsDictOpen(true)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: '1px solid #fde68a',
              backgroundColor: '#fef3c7',
              color: '#92400e',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              marginLeft: 'auto'
            }}
          >
            📖 Law Dictionary ({categoryCounts.dictionary.toLocaleString()} Terms)
          </button>
        </div>

        {/* Database Source Selector Tabs & View Mode / Scroll Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #cbd5e1', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          
          <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', boxSizing: 'border-box' }}>
            <button
              onClick={() => setActiveCategory('all')}
              style={{ 
                padding: '0.75rem 1.25rem', 
                borderRadius: '8px 8px 0 0', 
                border: 'none', 
                backgroundColor: activeCategory === 'all' ? '#0f172a' : 'transparent', 
                color: activeCategory === 'all' ? 'white' : '#64748b', 
                cursor: 'pointer', 
                fontWeight: '800', 
                fontSize: '0.9rem',
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem' 
              }}
            >
              <Layers size={18} /> All Results ({categoryCounts.all.toLocaleString()})
            </button>
            
            <button
              onClick={() => setActiveCategory('internal')}
              style={{ 
                padding: '0.75rem 1.25rem', 
                borderRadius: '8px 8px 0 0', 
                border: 'none', 
                backgroundColor: activeCategory === 'internal' ? '#0284c7' : 'transparent', 
                color: activeCategory === 'internal' ? 'white' : '#64748b', 
                cursor: 'pointer', 
                fontWeight: '800', 
                fontSize: '0.9rem',
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem' 
              }}
            >
              <Database size={18} /> Category 1: Own Database ({currentTabTotalCount.toLocaleString()})
            </button>

            <button
              onClick={() => setActiveCategory('external')}
              style={{ 
                padding: '0.75rem 1.25rem', 
                borderRadius: '8px 8px 0 0', 
                border: 'none', 
                backgroundColor: activeCategory === 'external' ? '#7c3aed' : 'transparent', 
                color: activeCategory === 'external' ? 'white' : '#64748b', 
                cursor: 'pointer', 
                fontWeight: '800', 
                fontSize: '0.9rem',
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem' 
              }}
            >
              <Globe size={18} /> Category 2: Other Sources (9,275 External Records)
            </button>
          </div>

          {/* VIEW MODE TOGGLE (GRID / TABLE / LIST) & SCROLL DOWN BUTTON */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingBottom: '0.5rem' }}>
            <button
              onClick={handleScrollDown}
              title="Click to scroll down directly to results grid / table and pagination"
              style={{
                backgroundColor: '#f1f5f9',
                color: '#0284c7',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                padding: '0.4rem 0.85rem',
                fontSize: '0.8rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem'
              }}
            >
              <ArrowDown size={15} /> Scroll Down
            </button>

            <div style={{ display: 'flex', backgroundColor: '#f1f5f9', borderRadius: '8px', padding: '0.2rem', border: '1px solid #cbd5e1' }}>
              <button
                onClick={() => setViewMode('grid')}
                title="Grid Form View (Multi-Column Responsive Cards)"
                style={{
                  backgroundColor: viewMode === 'grid' ? '#0284c7' : 'transparent',
                  color: viewMode === 'grid' ? 'white' : '#64748b',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.35rem 0.65rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  fontSize: '0.8rem',
                  fontWeight: '700'
                }}
              >
                <LayoutGrid size={15} /> Grid Form
              </button>
              <button
                onClick={() => setViewMode('table')}
                title="Table Form View (Structured Data Table)"
                style={{
                  backgroundColor: viewMode === 'table' ? '#0284c7' : 'transparent',
                  color: viewMode === 'table' ? 'white' : '#64748b',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.35rem 0.65rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  fontSize: '0.8rem',
                  fontWeight: '700'
                }}
              >
                <Table size={15} /> Table Form
              </button>
              <button
                onClick={() => setViewMode('list')}
                title="List Form View (Full Width Cards)"
                style={{
                  backgroundColor: viewMode === 'list' ? '#0284c7' : 'transparent',
                  color: viewMode === 'list' ? 'white' : '#64748b',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.35rem 0.65rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  fontSize: '0.8rem',
                  fontWeight: '700'
                }}
              >
                <List size={15} /> List Form
              </button>
            </div>
          </div>

        </div>

        {/* Loading Spinner */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <div style={{ display: 'inline-block', width: '42px', height: '42px', border: '4px solid #e2e8f0', borderTopColor: '#0284c7', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            <p style={{ marginTop: '1rem', color: '#64748b', fontWeight: '600' }}>
              Searching across {totalCount > 0 ? totalCount.toLocaleString() : '229,455'}+ legal precedents and external archives...
            </p>
          </div>
        )}

        {/* Search Results Rendering */}
        {!loading && (
          <div ref={resultsContainerRef}>
            {/* Active Filters Display with Active Count */}
            {(court || reporter || query || citation || writingJudge || counsel || startYear || endYear || searchScope !== 'all' || searchMode !== 'all' || sortOrder !== 'newest' || bifurcationTab !== 'all') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1.25rem', backgroundColor: '#f1f5f9', padding: '0.75rem 1rem', borderRadius: '10px' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: '800', color: '#475569' }}>Active Filters:</span>
                {bifurcationTab !== 'all' && (
                  <span style={{ fontSize: '0.8rem', backgroundColor: '#0f172a', color: '#ecc94b', padding: '0.3rem 0.75rem', borderRadius: '20px', fontWeight: '800', border: '1px solid #ecc94b' }}>
                    📑 Assigned Tab: {currentTabLabel} ({currentTabTotalCount.toLocaleString()} Total)
                  </span>
                )}
                {court && <span style={{ fontSize: '0.8rem', backgroundColor: '#0284c7', color: 'white', padding: '0.3rem 0.75rem', borderRadius: '20px', fontWeight: '700' }}>🏛️ Court: {court}</span>}
                {reporter && <span style={{ fontSize: '0.8rem', backgroundColor: '#0284c7', color: 'white', padding: '0.3rem 0.75rem', borderRadius: '20px', fontWeight: '700' }}>📖 Journal: {reporter}</span>}
                {query && <span style={{ fontSize: '0.8rem', backgroundColor: '#0284c7', color: 'white', padding: '0.3rem 0.75rem', borderRadius: '20px', fontWeight: '700' }}>🔍 Query: {query}</span>}
                {searchScope !== 'all' && <span style={{ fontSize: '0.8rem', backgroundColor: '#0284c7', color: 'white', padding: '0.3rem 0.75rem', borderRadius: '20px', fontWeight: '700' }}>🎯 Scope: {searchScope}</span>}
                {searchMode !== 'all' && <span style={{ fontSize: '0.8rem', backgroundColor: '#7c3aed', color: 'white', padding: '0.3rem 0.75rem', borderRadius: '20px', fontWeight: '700' }}>⚙️ Mode: {searchMode}</span>}
                {(startYear || endYear) && <span style={{ fontSize: '0.8rem', backgroundColor: '#0891b2', color: 'white', padding: '0.3rem 0.75rem', borderRadius: '20px', fontWeight: '700' }}>📅 Years: {startYear || '1947'} - {endYear || '2026'}</span>}
                {sortOrder !== 'newest' && <span style={{ fontSize: '0.8rem', backgroundColor: '#059669', color: 'white', padding: '0.3rem 0.75rem', borderRadius: '20px', fontWeight: '700' }}>↕️ Sort: {sortOrder}</span>}
                {citation && <span style={{ fontSize: '0.8rem', backgroundColor: '#0284c7', color: 'white', padding: '0.3rem 0.75rem', borderRadius: '20px', fontWeight: '700' }}>🎯 Citation: {citation}</span>}
                {writingJudge && <span style={{ fontSize: '0.8rem', backgroundColor: '#0284c7', color: 'white', padding: '0.3rem 0.75rem', borderRadius: '20px', fontWeight: '700' }}>⚖️ Judge: {writingJudge}</span>}
                {counsel && <span style={{ fontSize: '0.8rem', backgroundColor: '#0284c7', color: 'white', padding: '0.3rem 0.75rem', borderRadius: '20px', fontWeight: '700' }}>💼 Counsel: {counsel}</span>}
                <button 
                  onClick={() => { setBifurcationTab('all'); handleResetFilters(); }}
                  style={{ marginLeft: 'auto', backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '0.35rem 0.85rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer' }}
                >
                  Clear All Filters
                </button>
              </div>
            )}

            {/* CATEGORY 1: Results of Own Database with Exact Count Pane */}
            {(activeCategory === 'all' || activeCategory === 'internal') && (
              <div style={{ marginBottom: '3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', backgroundColor: '#e0f2fe', padding: '1rem 1.25rem', borderRadius: '12px', borderLeft: '6px solid #0284c7', flexWrap: 'wrap', gap: '0.75rem', boxShadow: '0 2px 6px rgba(2, 132, 199, 0.08)' }}>
                  <div>
                    <h2 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#0369a1', margin: '0 0 0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Database size={22} color="#0284c7" /> Category 1: Own Database — {currentTabLabel}
                    </h2>
                    <div style={{ fontSize: '0.85rem', color: '#0369a1', fontWeight: '600' }}>
                      Total in Repository: <strong style={{ color: '#0f172a', fontSize: '0.95rem' }}>{currentTabTotalCount.toLocaleString()} Records</strong> | Showing Page {page} of {Math.max(1, Math.ceil(currentTabTotalCount / 15)).toLocaleString()} ({displayedInternalDocs.length} items in {viewMode.toUpperCase()} Form)
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ backgroundColor: '#0284c7', color: 'white', padding: '0.35rem 0.85rem', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '800', boxShadow: '0 2px 4px rgba(2, 132, 199, 0.2)' }}>
                      📊 Total Count: {currentTabTotalCount.toLocaleString()}
                    </span>
                    <span style={{ backgroundColor: '#ffffff', color: '#0369a1', border: '1px solid #bae6fd', padding: '0.35rem 0.75rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '700' }}>
                      S# {(page - 1) * 15 + 1} to {(page - 1) * 15 + displayedInternalDocs.length}
                    </span>
                  </div>
                </div>

                {displayedInternalDocs.length === 0 ? (
                  <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', textAlign: 'center', color: '#64748b' }}>
                    No internal database documents found under the selected bifurcation tab.
                  </div>
                ) : viewMode === 'table' ? (
                  /* TABLE FORM VIEW */
                  <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', maxHeight: '72vh', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#0f172a', color: 'white', borderBottom: '2px solid #0284c7' }}>
                          <th style={{ padding: '0.85rem 1rem', width: '60px', textAlign: 'center' }}>S#</th>
                          <th style={{ padding: '0.85rem 1rem', width: '170px' }}>Classification</th>
                          <th style={{ padding: '0.85rem 1rem' }}>Citation & Case Title</th>
                          <th style={{ padding: '0.85rem 1rem', width: '200px' }}>Court, Year & Bench</th>
                          <th style={{ padding: '0.85rem 1rem', width: '280px' }}>Key Holdings / Summary</th>
                          <th style={{ padding: '0.85rem 1rem', width: '170px', textAlign: 'center' }}>Actions & QR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedInternalDocs.map((doc, idx) => {
                          const serialNum = (page - 1) * 15 + (idx + 1);
                          const qrUrl = generateQRUrl(doc);
                          const badgeInfo = getDocBifurcationBadge(doc);
                          return (
                            <tr key={doc._id || idx} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                              <td style={{ padding: '0.85rem 1rem', textAlign: 'center', fontWeight: '800', color: '#0f172a' }}>
                                <span style={{ backgroundColor: '#0f172a', color: 'white', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                                  {serialNum}
                                </span>
                              </td>
                              <td style={{ padding: '0.85rem 1rem' }}>
                                <span style={{ background: badgeInfo.bg, color: badgeInfo.color, border: `1px solid ${badgeInfo.border}`, padding: '0.25rem 0.55rem', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '800', display: 'inline-block' }}>
                                  {badgeInfo.label}
                                </span>
                              </td>
                              <td style={{ padding: '0.85rem 1rem' }}>
                                <button onClick={() => setSelectedDoc(doc)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', color: '#0284c7', fontWeight: '800', fontSize: '0.95rem', textDecoration: 'underline' }}>
                                  {doc.citationNumber || 'Citation Pending'} — {doc.title}
                                </button>
                                {doc.wordCount ? (
                                  <div style={{ marginTop: '0.35rem' }}>
                                    <span style={{
                                      backgroundColor: (doc.textLength >= 8000 || doc.wordCount >= 1300) ? '#dcfce7' : ((doc.textLength >= 2000 || doc.wordCount >= 330) ? '#e0f2fe' : '#f1f5f9'),
                                      color: (doc.textLength >= 8000 || doc.wordCount >= 1300) ? '#166534' : ((doc.textLength >= 2000 || doc.wordCount >= 330) ? '#0369a1' : '#475569'),
                                      border: `1px solid ${(doc.textLength >= 8000 || doc.wordCount >= 1300) ? '#86efac' : ((doc.textLength >= 2000 || doc.wordCount >= 330) ? '#bae6fd' : '#cbd5e1')}`,
                                      fontSize: '0.68rem',
                                      fontWeight: '800',
                                      padding: '0.15rem 0.5rem',
                                      borderRadius: '4px',
                                      display: 'inline-block'
                                    }}>
                                      {(doc.textLength >= 8000 || doc.wordCount >= 1300) ? '🌟 Full Judgment' : ((doc.textLength >= 2000 || doc.wordCount >= 330) ? '📜 Complete' : '📑 Short Order')} ({doc.wordCount.toLocaleString()} words)
                                    </span>
                                  </div>
                                ) : null}
                              </td>
                              <td style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', color: '#475569' }}>
                                <div><strong>🏛️ {doc.court}</strong></div>
                                <div>📅 Year: {doc.year || '2026'} | 📖 {doc.category || 'SCMR'}</div>
                                {doc.metadata?.writing_judge && (
                                  <div style={{ color: '#92400e', fontWeight: '700', marginTop: '0.2rem' }}>✍️ {doc.metadata.writing_judge}</div>
                                )}
                              </td>
                              <td style={{ padding: '0.85rem 1rem', fontSize: '0.82rem', color: '#334155', lineHeight: '1.4' }}>
                                {doc.metadata?.conclusion_summary?.substring(0, 130) || doc.summary?.substring(0, 130) || doc.content?.substring(0, 130)}...
                              </td>
                              <td style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'center' }}>
                                  <button onClick={() => setSelectedDoc(doc)} style={{ width: '100%', backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
                                    <BookOpen size={12} /> Read Full Text
                                  </button>
                                  <button onClick={() => setQrModalDoc(doc)} style={{ width: '100%', backgroundColor: '#ffffff', color: '#0284c7', border: '1px solid #bae6fd', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
                                    <QrCode size={12} /> QR Share
                                  </button>
                                  <button onClick={() => triggerDownload(doc._id)} style={{ width: '100%', backgroundColor: '#0f172a', color: 'white', border: 'none', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
                                    <Download size={12} /> PDF
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  /* GRID OR LIST FORM VIEW WITH SMOOTH SCROLL PANE */
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(460px, 1fr))' : '1fr', 
                    gap: '1.25rem',
                    maxHeight: '72vh',
                    overflowY: 'auto',
                    paddingRight: '6px'
                  }}>
                    {displayedInternalDocs.map((doc, idx) => {
                      const serialNum = (page - 1) * 15 + (idx + 1);
                      const qrUrl = generateQRUrl(doc);
                      const badgeInfo = getDocBifurcationBadge(doc);
                      return (
                        <div 
                          key={doc._id || idx}
                          style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            padding: '1.25rem',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            gap: '0.85rem',
                            boxSizing: 'border-box',
                            transition: 'transform 0.15s, box-shadow 0.15s'
                          }}
                        >
                          <div>
                            {/* Card Top Row: S# Badge, Richness Badge & Assigned Bifurcation Badge */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap', gap: '0.4rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                <span style={{ display: 'inline-block', backgroundColor: '#0f172a', color: 'white', fontWeight: '800', fontSize: '0.78rem', padding: '0.25rem 0.65rem', borderRadius: '6px' }}>
                                  S# {serialNum}
                                </span>
                                {doc.wordCount ? (
                                  <span style={{
                                    backgroundColor: (doc.textLength >= 8000 || doc.wordCount >= 1300) ? '#dcfce7' : ((doc.textLength >= 2000 || doc.wordCount >= 330) ? '#e0f2fe' : '#f1f5f9'),
                                    color: (doc.textLength >= 8000 || doc.wordCount >= 1300) ? '#166534' : ((doc.textLength >= 2000 || doc.wordCount >= 330) ? '#0369a1' : '#475569'),
                                    border: `1px solid ${(doc.textLength >= 8000 || doc.wordCount >= 1300) ? '#86efac' : ((doc.textLength >= 2000 || doc.wordCount >= 330) ? '#bae6fd' : '#cbd5e1')}`,
                                    fontSize: '0.7rem',
                                    fontWeight: '800',
                                    padding: '0.2rem 0.55rem',
                                    borderRadius: '6px'
                                  }}>
                                    {(doc.textLength >= 8000 || doc.wordCount >= 1300) ? '🌟 Full Judgment' : ((doc.textLength >= 2000 || doc.wordCount >= 330) ? '📜 Complete' : '📑 Short Order')} ({doc.wordCount.toLocaleString()}w)
                                  </span>
                                ) : null}
                              </div>
                              <span 
                                style={{ 
                                  background: badgeInfo.bg, 
                                  color: badgeInfo.color, 
                                  border: `1px solid ${badgeInfo.border}`, 
                                  padding: '0.25rem 0.65rem', 
                                  borderRadius: '6px', 
                                  fontSize: '0.72rem', 
                                  fontWeight: '800', 
                                  letterSpacing: '0.5px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.35rem',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                              >
                                {badgeInfo.label}
                              </span>
                            </div>

                            {/* Case Citation & Title */}
                            <div style={{ marginBottom: '0.5rem' }}>
                              <button
                                onClick={() => setSelectedDoc(doc)}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', width: '100%' }}
                              >
                                <h3 style={{ fontSize: '1.08rem', fontWeight: '800', color: '#0284c7', margin: '0 0 0.35rem 0', textDecoration: 'underline', lineHeight: '1.4' }}>
                                  {doc.citationNumber || 'Citation Pending'} — {doc.title}
                                </h3>
                              </button>
                            </div>

                            {/* Metadata Tags */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', fontSize: '0.75rem', color: '#475569', fontWeight: '700', marginBottom: '0.65rem' }}>
                              <span style={{ backgroundColor: '#f1f5f9', padding: '0.2rem 0.55rem', borderRadius: '4px' }}>
                                🏛️ {doc.court}
                              </span>
                              <span style={{ backgroundColor: '#f1f5f9', padding: '0.2rem 0.55rem', borderRadius: '4px' }}>
                                📅 Year: {doc.year || '2026'}
                              </span>
                              {doc.category && (
                                <span style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '0.2rem 0.55rem', borderRadius: '4px', color: '#64748b' }}>
                                  📖 {doc.category}
                                </span>
                              )}
                              {doc.metadata?.writing_judge && (
                                <span style={{ backgroundColor: '#fef3c7', color: '#92400e', padding: '0.2rem 0.55rem', borderRadius: '4px' }}>
                                  ✍️ {doc.metadata.writing_judge}
                                </span>
                              )}
                            </div>

                            {/* Brief Case History / Summary */}
                            <div style={{ backgroundColor: '#f8fafc', padding: '0.65rem 0.85rem', borderRadius: '8px', borderLeft: '3px solid #38bdf8', fontSize: '0.85rem', color: '#334155', lineHeight: '1.45' }}>
                              <strong>Brief Case History & Summary: </strong>
                              {doc.metadata?.conclusion_summary || doc.content?.substring(0, 190) || doc.summary || 'Summary unavailable. Click to read full judgment text.'}...
                            </div>
                          </div>

                          {/* Card Bottom: Action Buttons & QR Scan */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.75rem', borderTop: '1px solid #f1f5f9', gap: '0.6rem', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => setSelectedDoc(doc)}
                                style={{ backgroundColor: '#0284c7', color: 'white', padding: '0.4rem 0.75rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                              >
                                <BookOpen size={14} /> Full Judgment Text
                              </button>
                              {doc.headnotes && (
                                <button
                                  onClick={() => setSelectedDoc(doc)}
                                  style={{ backgroundColor: '#166534', color: 'white', padding: '0.4rem 0.75rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                                >
                                  <Scale size={14} /> Headnotes
                                </button>
                              )}
                              <button
                                onClick={() => setQrModalDoc(doc)}
                                title="Click to view and scan live QR Code transfer for this case law"
                                style={{ backgroundColor: '#ffffff', color: '#0284c7', border: '1px solid #bae6fd', padding: '0.4rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                              >
                                <QrCode size={14} color="#0284c7" /> QR Share
                              </button>
                              <button
                                onClick={() => triggerDownload(doc._id)}
                                style={{ backgroundColor: '#0f172a', color: 'white', padding: '0.4rem 0.75rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                              >
                                <Download size={14} /> PDF
                              </button>
                            </div>

                            <div style={{ textAlign: 'center', backgroundColor: '#f8fafc', padding: '0.35rem', borderRadius: '6px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                              <img src={qrUrl} alt="QR Code" style={{ width: '45px', height: '45px', display: 'block', borderRadius: '4px' }} />
                              <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: '700' }}>
                                QR Scan
                              </span>
                            </div>
                          </div>

                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* CATEGORY 2: Results from Other Sources in Grid / Table / List Form */}
            {(activeCategory === 'all' || activeCategory === 'external') && (
              <div style={{ marginBottom: '3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', backgroundColor: '#f3e8ff', padding: '1rem 1.25rem', borderRadius: '12px', borderLeft: '6px solid #7c3aed', flexWrap: 'wrap', gap: '0.75rem', boxShadow: '0 2px 6px rgba(124, 58, 237, 0.08)' }}>
                  <div>
                    <h2 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#6b21a8', margin: '0 0 0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Globe size={22} color="#7c3aed" /> Category 2: Results from Other Sources & External Archives
                    </h2>
                    <div style={{ fontSize: '0.85rem', color: '#6b21a8', fontWeight: '600' }}>
                      Total in External Archive: <strong style={{ color: '#0f172a', fontSize: '0.95rem' }}>9,275 Records</strong> | Showing {displayedExternalDocs.length} Live Results
                    </div>
                  </div>

                  <span style={{ backgroundColor: '#7c3aed', color: 'white', padding: '0.35rem 0.85rem', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '800', boxShadow: '0 2px 4px rgba(124, 58, 237, 0.2)' }}>
                    🌐 Total External: 9,275
                  </span>
                </div>

                {displayedExternalDocs.length === 0 ? (
                  <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', textAlign: 'center', color: '#64748b' }}>
                    No external sources found under the selected bifurcation tab.
                  </div>
                ) : (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(460px, 1fr))' : '1fr', 
                    gap: '1.25rem',
                    maxHeight: '72vh',
                    overflowY: 'auto',
                    paddingRight: '6px'
                  }}>
                    {displayedExternalDocs.map((ext, idx) => {
                      const extSerialNum = idx + 1;
                      const targetUrl = ext.externalUrl || ext.source_url || ext.url || ext.external_link || ext.link || `https://www.supremecourt.gov.pk/downloads_judgements/all_judgments.php`;
                      const extQrUrl = generateQRUrl(ext);
                      const extBadge = getDocBifurcationBadge(ext);
                      return (
                        <div 
                          key={ext.id || idx}
                          style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            border: '1px solid #e9d5ff',
                            padding: '1.25rem',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            gap: '0.85rem',
                            boxSizing: 'border-box',
                            transition: 'transform 0.15s, box-shadow 0.15s'
                          }}
                        >
                          <div>
                            {/* Card Top Row: S# Badge & Assigned Bifurcation Badge */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap', gap: '0.4rem' }}>
                              <span style={{ display: 'inline-block', backgroundColor: '#7c3aed', color: 'white', fontWeight: '800', fontSize: '0.78rem', padding: '0.25rem 0.65rem', borderRadius: '6px' }}>
                                S# {extSerialNum}
                              </span>
                              <span 
                                style={{ 
                                  background: extBadge.bg, 
                                  color: extBadge.color, 
                                  border: `1px solid ${extBadge.border}`, 
                                  padding: '0.25rem 0.65rem', 
                                  borderRadius: '6px', 
                                  fontSize: '0.72rem', 
                                  fontWeight: '800', 
                                  letterSpacing: '0.5px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.35rem',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                              >
                                {extBadge.label}
                              </span>
                            </div>

                            {/* External Hyperlinked Citation & Title */}
                            <div style={{ marginBottom: '0.5rem' }}>
                              <a
                                href={targetUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#7c3aed', textDecoration: 'underline', display: 'block' }}
                              >
                                <h3 style={{ fontSize: '1.08rem', fontWeight: '800', margin: '0 0 0.35rem 0', display: 'flex', alignItems: 'center', gap: '0.4rem', lineHeight: '1.4' }}>
                                  {ext.citationNumber} — {ext.title} <ExternalLink size={15} />
                                </h3>
                              </a>
                            </div>

                            {/* Metadata Tags */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', fontSize: '0.75rem', color: '#475569', fontWeight: '700', marginBottom: '0.65rem' }}>
                              <span style={{ backgroundColor: '#f3e8ff', color: '#6b21a8', padding: '0.2rem 0.55rem', borderRadius: '4px' }}>
                                🌐 {ext.sourceName}
                              </span>
                              <span style={{ backgroundColor: '#f1f5f9', padding: '0.2rem 0.55rem', borderRadius: '4px' }}>
                                🏛️ {ext.court}
                              </span>
                              <span style={{ backgroundColor: '#f1f5f9', padding: '0.2rem 0.55rem', borderRadius: '4px' }}>
                                📅 Year: {ext.year || '2025'}
                              </span>
                            </div>

                            {/* Brief Case History */}
                            <div style={{ backgroundColor: '#faf5ff', padding: '0.65rem 0.85rem', borderRadius: '8px', borderLeft: '3px solid #a855f7', fontSize: '0.85rem', color: '#334155', lineHeight: '1.45' }}>
                              <strong>Brief Case History & Summary: </strong>
                              {ext.briefHistory}
                            </div>
                          </div>

                          {/* Card Bottom: External Action & QR Scan Link */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.75rem', borderTop: '1px solid #f3e8ff', gap: '0.6rem', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <a
                                href={targetUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ backgroundColor: '#7c3aed', color: 'white', padding: '0.4rem 0.85rem', borderRadius: '6px', textDecoration: 'none', fontSize: '0.8rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                              >
                                Open Source <ExternalLink size={14} />
                              </a>
                              <button
                                onClick={() => setQrModalDoc(ext)}
                                title="Click to view and scan live QR Code transfer for this case law"
                                style={{ backgroundColor: '#ffffff', color: '#7c3aed', border: '1px solid #e9d5ff', padding: '0.4rem 0.85rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                              >
                                <QrCode size={14} color="#7c3aed" /> QR Share
                              </button>
                            </div>

                            <a
                              href={targetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ textAlign: 'center', backgroundColor: '#faf5ff', padding: '0.35rem', borderRadius: '6px', border: '1px solid #e9d5ff', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                            >
                              <img src={extQrUrl} alt="QR Code" style={{ width: '45px', height: '45px', display: 'block', borderRadius: '4px' }} />
                              <span style={{ fontSize: '0.65rem', color: '#7c3aed', fontWeight: '700' }}>
                                QR Link
                              </span>
                            </a>
                          </div>

                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '2rem' }}>
                <button
                  disabled={page === 1}
                  onClick={() => fetchDocuments(page - 1)}
                  style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: page === 1 ? '#f1f5f9' : 'white', cursor: page === 1 ? 'not-allowed' : 'pointer', fontWeight: '600' }}
                >
                  <ChevronLeft size={18} /> Previous
                </button>
                <span style={{ fontWeight: '700', color: '#475569' }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  disabled={page === totalPages}
                  onClick={() => fetchDocuments(page + 1)}
                  style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: page === totalPages ? '#f1f5f9' : 'white', cursor: page === totalPages ? 'not-allowed' : 'pointer', fontWeight: '600' }}
                >
                  Next <ChevronRight size={18} />
                </button>
              </div>
            )}

          </div>
        )}

      </div>

      {/* FLOATING QUICK SCROLL CONTROLS (SCROLL DOWN & TOP) */}
      <div style={{ position: 'fixed', bottom: '24px', right: '24px', display: 'flex', flexDirection: 'column', gap: '0.5rem', zIndex: 999 }}>
        <button
          onClick={handleScrollDown}
          title="Scroll Down to Results Grid & Pagination"
          style={{
            backgroundColor: '#0284c7',
            color: 'white',
            border: '2px solid white',
            borderRadius: '50%',
            width: '42px',
            height: '42px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(2, 132, 199, 0.4)',
            cursor: 'pointer',
            transition: 'transform 0.15s'
          }}
        >
          <ArrowDown size={20} />
        </button>

        {showScrollTop && (
          <button
            onClick={handleScrollToTop}
            title="Scroll Back to Top"
            style={{
              backgroundColor: '#0f172a',
              color: 'white',
              border: '2px solid white',
              borderRadius: '50%',
              width: '42px',
              height: '42px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.4)',
              cursor: 'pointer',
              transition: 'transform 0.15s'
            }}
          >
            <ArrowUp size={20} />
          </button>
        )}
      </div>

      {/* Instant View Modal Overlay */}
      {selectedDoc && (
        <DocumentDetailModal
          document={selectedDoc}
          onClose={() => setSelectedDoc(null)}
        />
      )}

      {/* INSTANT CASE LAW QR SHARE MODAL */}
      {qrModalDoc && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(6px)',
          zIndex: 3000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            maxWidth: '460px',
            width: '100%',
            padding: '1.75rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '2px solid #0284c7',
            position: 'relative'
          }}>
            <button
              onClick={() => setQrModalDoc(null)}
              style={{
                position: 'absolute',
                top: '1rem', right: '1rem',
                backgroundColor: '#f1f5f9',
                border: 'none',
                borderRadius: '50%',
                width: '32px', height: '32px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: '#64748b'
              }}
            >
              <X size={18} />
            </button>

            <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
              <div style={{ backgroundColor: '#e0f2fe', color: '#0369a1', display: 'inline-flex', padding: '0.4rem 0.8rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '800', marginBottom: '0.5rem' }}>
                📷 LIVE SCANNABLE CASE LAW QR CODE
              </div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', margin: '0 0 0.35rem 0', lineHeight: '1.3' }}>
                {qrModalDoc.citationNumber || qrModalDoc.citation || 'Case Citation'} — {qrModalDoc.title}
              </h2>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                {qrModalDoc.court || 'High Court / Supreme Court'} | Year: {qrModalDoc.year || '2026'}
              </p>
            </div>

            {/* QR CODE CONTAINER */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              padding: '1.25rem',
              border: '1px solid #cbd5e1',
              marginBottom: '1.25rem'
            }}>
              <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <QRCodeSVG
                  value={typeof window !== 'undefined' ? `${window.location.origin}/law-directory?id=${encodeURIComponent(qrModalDoc.id || qrModalDoc._id || qrModalDoc.citationNumber || qrModalDoc.citation)}` : `/law-directory?id=${encodeURIComponent(qrModalDoc.id || qrModalDoc._id || qrModalDoc.citationNumber || qrModalDoc.citation)}`}
                  size={180}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.78rem', color: '#475569', fontWeight: '600', textAlign: 'center' }}>
                Point mobile camera or WhatsApp to open and transfer case precedent instantly across devices.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => {
                  const url = typeof window !== 'undefined' ? `${window.location.origin}/law-directory?id=${encodeURIComponent(qrModalDoc.id || qrModalDoc._id || qrModalDoc.citationNumber || qrModalDoc.citation)}` : `/law-directory?id=${encodeURIComponent(qrModalDoc.id || qrModalDoc._id || qrModalDoc.citationNumber || qrModalDoc.citation)}`;
                  navigator.clipboard.writeText(url);
                  setCopiedQrLink(true);
                  setTimeout(() => setCopiedQrLink(false), 2500);
                }}
                style={{
                  flex: 1,
                  padding: '0.65rem',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: copiedQrLink ? '#10b981' : '#0284c7',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.4rem'
                }}
              >
                {copiedQrLink ? <Check size={16} /> : <Copy size={16} />}
                {copiedQrLink ? 'Direct Link Copied!' : 'Copy Direct Link'}
              </button>

              <button
                onClick={() => {
                  const targetDoc = qrModalDoc;
                  setQrModalDoc(null);
                  setSelectedDoc(targetDoc);
                }}
                style={{
                  padding: '0.65rem 1rem',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#0f172a',
                  color: '#ecc94b',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                Instant View
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Black's Law Dictionary Modal */}
      {isDictOpen && (
        <BlacksLawDictionaryModal
          isOpen={isDictOpen}
          onClose={() => setIsDictOpen(false)}
        />
      )}

    </div>
  );

  if (isModalView) {
    return (
      <div style={{
        position: 'fixed',
        top: windowState === 'fullscreen' ? 0 : 'auto',
        left: windowState === 'fullscreen' ? 0 : 'auto',
        right: windowState === 'fullscreen' ? 0 : '24px',
        bottom: windowState === 'fullscreen' ? 0 : '24px',
        width: windowState === 'fullscreen' ? '100vw' : `${windowSize.width}px`,
        height: windowState === 'fullscreen' ? '100vh' : `${windowSize.height}px`,
        maxWidth: windowState === 'fullscreen' ? '100vw' : '96vw',
        maxHeight: windowState === 'fullscreen' ? '100vh' : '94vh',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(10px)',
        zIndex: 2500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: windowState === 'fullscreen' ? 0 : '0.5rem',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        <div style={{
          backgroundColor: colors.cardBg,
          color: colors.textColor,
          border: `2px solid ${colors.goldPrimary}`,
          borderRadius: windowState === 'fullscreen' ? '0' : '16px',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0,0,0,0.8), 0 0 30px rgba(5, 150, 105, 0.25)',
          overflow: 'hidden',
          boxSizing: 'border-box',
          position: 'relative'
        }}>
          {content}

          {/* CLICK & DRAG 2D RESIZE HANDLE (HORIZONTAL & VERTICAL) AT BOTTOM-RIGHT CORNER */}
          {windowState !== 'fullscreen' && (
            <div 
              onMouseDown={handleStartResize}
              title="Click and drag horizontally or vertically to resize window size"
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '20px',
                height: '20px',
                cursor: 'nwse-resize',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#059669',
                color: 'white',
                borderRadius: '4px 0 14px 0',
                userSelect: 'none'
              }}
            >
              <span style={{ fontSize: '11px', fontWeight: 'bold', lineHeight: 1 }}>↘</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return content;
};

export default LawDirectory;
